/**
 * @module storage
 * @description File storage and management with filesystem and S3 drivers
 */

import { tmpdir } from 'node:os'
import { join } from 'node:path'
import { readFile, unlink } from 'node:fs/promises'
import type { ModuleManifest, ModuleContext, LoggerReporter } from '@gzl10/nexus-sdk'
import type { SchedulesApi } from '../schedules/index.js'
import { storageConfigEntity, storageFilesEntity } from './storage.entity.js'
import { fileAttachmentsEntity } from './attachments.entity.js'
import { createAttachmentsService } from './attachments.service.js'
import { storageActions } from './actions/index.js'
import { initStorageService } from './storage.service.js'
import { initImageProcessingService, getImageProcessingService } from './image-processing.service.js'
import { initVideoProcessingService, getVideoProcessingService } from './video-processing.service.js'
import { buildStorageConfig } from './storage.config.js'
import { createStorageRoutes } from './storage.routes.js'

// Re-exports
export { StorageService, getStorageService } from './storage.service.js'
export type { StorageConfig } from './storage.config.js'
export type { StorageDriver, StorageFile, PutOptions } from './drivers/driver.interface.js'
export { FilesystemDriver } from './drivers/filesystem.driver.js'
export { S3Driver } from './drivers/s3.driver.js'
export { storageConfigEntity, storageFilesEntity } from './storage.entity.js'
export { fileAttachmentsEntity } from './attachments.entity.js'
export { createAttachmentsService, type AttachmentsService } from './attachments.service.js'
export type { FileAttachment, AttachedFile, AttachedEntity } from './attachments.types.js'
export { storageActions, uploadAction } from './actions/index.js'
export { ImageProcessingService, getImageProcessingService, type ResizeOptions } from './image-processing.service.js'
export { VideoProcessingService, getVideoProcessingService, initVideoProcessingService, isVideoMimetype } from './video-processing.service.js'
export type { ThumbnailOptions, PreviewOptions, TranscodeOptions, VideoResult } from './video-processing.service.js'
export { detectFfmpeg, type FfmpegAvailability, parseTimestamp } from './ffmpeg.utils.js'
export type { VideoMetadata } from './storage.types.js'
export type { ResolvedFile } from './storage.service.js'
export { resolveVideoFile } from './video.helpers.js'

// Types from storage.types.ts
export type {
  FilesystemMetadata,
  S3Metadata,
  UploadedFile,
  UploadOptions,
  StorageFileRecord
} from './storage.types.js'

export const storageModule: ModuleManifest = {
  name: 'storage',
  label: { en: 'Storage', es: 'Almacenamiento' },
  icon: 'mdi:cloud-upload-outline',
  description: { en: 'File storage and management', es: 'Almacenamiento y gestión de archivos' },
  type: 'core',
  category: 'assets',
  dependencies: ['logger'],

  definitions: [storageConfigEntity, storageFilesEntity, fileAttachmentsEntity],

  actions: storageActions,

  pages: [
    {
      id: 'storage-dashboard',
      label: { en: 'Overview', es: 'Resumen' },
      icon: 'mdi:chart-pie',
      type: 'dashboard',
      order: 0,
      widgets: [
        { id: 'stats', type: 'stat', endpoint: '/storage/stats', layout: { span: 12 } },
        { id: 'by-type', type: 'chart', label: { en: 'By file type', es: 'Por tipo de archivo' }, endpoint: '/storage/stats/by-type', chart: 'donut', layout: { span: 6 } },
        { id: 'by-folder', type: 'chart', label: { en: 'By folder', es: 'Por carpeta' }, endpoint: '/storage/stats/by-folder', chart: 'bar', layout: { span: 6 } }
      ]
    }
  ],

  routePrefix: '/storage',

  init: async (ctx: ModuleContext) => {
    const projectPath = ctx.core.getProjectPath()

    // Read default config from CollectionService (envDefaults applied automatically)
    const configService = ctx.services.get<{ findAll: (query?: any) => Promise<{ items: Record<string, unknown>[] }> }>('storage_configs')
    const result = await configService.findAll({ pageSize: 100 })
    const defaultRow = result.items.find(r => r['scope'] === 'default') ?? null
    const config = buildStorageConfig(defaultRow, projectPath, ctx.core.generateId)

    // Scope config lookup for multi-scope storage
    const getScopeConfig = async (scope: string) => {
      const r = await configService.findAll({ filters: { scope } })
      const row = r.items[0]
      return row ? buildStorageConfig(row, projectPath, ctx.core.generateId) : null
    }

    // Inicializar y registrar el servicio de storage
    const loggerService = ctx.services.getOptional<LoggerReporter>('logger')
    const storageService = initStorageService({
      db: ctx.db.knex,
      config,
      logger: ctx.core.logger,
      errors: ctx.core.errors,
      nowTimestamp: ctx.db.nowTimestamp,
      loggerService,
      getScopeConfig
    })
    ctx.services.register('storage', storageService)
    // Register config for routes/helpers that need maxFileSize
    ctx.services.register('storageConfig', config)

    // Initialize image processing service (resize + cache)
    const imageService = initImageProcessingService(
      config.resize,
      ctx.core.logger
    )
    ctx.services.register('imageProcessing', imageService)

    // Initialize video processing service (ffmpeg-based)
    const videoService = initVideoProcessingService(config.video, ctx.core.logger)
    ctx.services.register('videoProcessing', videoService)

    // Non-blocking ffmpeg detection
    videoService.isAvailable().then(available => {
      if (!available) ctx.core.logger.info('Video processing disabled (ffmpeg not found)')
    }).catch(() => {})

    // ── File Attachments ──
    // Access check listeners auto-registered by engine (attachable + taggeable on storageFilesEntity)
    const attachmentsService = createAttachmentsService(ctx)
    ctx.services.register('attachments', attachmentsService)

    // Cleanup: entity.deleted → remove pivots (both directions)
    ctx.events.on('entity.deleted', async (...args: unknown[]) => {
      const payload = args[0] as { module: string; entity: string; id: string }
      const entityType = `${payload.module}.${payload.entity}`
      try {
        await attachmentsService.removeAllForEntity(entityType, payload.id)
        if (payload.module === 'storage' && payload.entity === 'files') {
          await attachmentsService.removeAllForFile(payload.id)
          // Invalidate image + video cache
          try { const imgSvc = getImageProcessingService(); await imgSvc.invalidateCache(payload.id) } catch { /* best-effort */ }
          try { const vidSvc = getVideoProcessingService(); await vidSvc.invalidateCache(payload.id) } catch { /* best-effort */ }
        }
      } catch (err) {
        ctx.core.logger.warn(`Storage: failed to cleanup attachments for ${entityType}:${payload.id}: ${err}`)
      }
    })

    // TODO: add storage:cleanup-temp-files when temp file tracking is implemented
    // storage_files has no is_temp column — visibility field does not identify temporals
    ctx.events.on('schedules.ready', (...args: unknown[]) => {
      const schedules = args[0] as SchedulesApi

      schedules.registerFunction('storage.transcode', async (fnArgs?: Record<string, unknown>) => {
        const fileId = fnArgs?.['fileId'] as string
        const format = (fnArgs?.['format'] as string) || 'mp4'
        const quality = (fnArgs?.['quality'] as string) || 'medium'

        if (!fileId) { ctx.core.logger.warn('storage.transcode: missing fileId'); return }

        const videoSvc = getVideoProcessingService()
        if (!await videoSvc.isAvailable()) {
          ctx.core.logger.warn('storage.transcode: ffmpeg not available'); return
        }

        const record = await storageService.getById(fileId)
        if (!record) { ctx.core.logger.warn({ fileId }, 'storage.transcode: file not found'); return }

        const resolved = await storageService.resolveInputFile(fileId)
        const ext = format === 'webm' ? '.webm' : '.mp4'
        const outputPath = join(tmpdir(), `nexus-transcode-out-${fileId}${ext}`)

        try {
          const result = await videoSvc.transcode(resolved.path, outputPath, {
            format: format as 'mp4' | 'webm',
            quality: quality as 'low' | 'medium' | 'high'
          })

          // LIMITATION: reads entire transcoded output into memory for upload().
          // upload() is buffer-based (multer pattern). maxTranscodeSize (500MB) limits risk.
          const outputBuffer = await readFile(outputPath)
          const newFilename = record.filename.replace(/\.[^.]+$/, `-transcoded${ext}`)

          await storageService.upload({
            fieldname: 'file',
            originalname: newFilename,
            mimetype: result.mimetype,
            buffer: outputBuffer,
            size: outputBuffer.length
          }, {
            folder: record.folder,
            userId: record.created_by || undefined,
            visibility: 'internal'
          })

          ctx.core.logger.info({ fileId, format, outputSize: outputBuffer.length }, 'Video transcoded and saved')
        } finally {
          await resolved.cleanup()
          await unlink(outputPath).catch(() => {})
        }
      })
    })

    ctx.core.logger.debug('Storage service registered')
  },

  routes: (ctx: ModuleContext) => createStorageRoutes(ctx)
}
